import React, { useState, useEffect, createContext, FC, ReactNode, useContext } from 'react'
import { api } from './network/api'

import { Alert } from "@mui/material"
import { Navigate } from "react-router-dom"
import Cookies from "js-cookie"
import { HeroData } from './heropage/buildsys'
import { TeamData } from './teampage/team'

export interface UserData {
    name: string
    heros: number[]
    role: number
    activeHeroID?: number
}

export interface PrivateRouteProps {
    auth: string
    children: JSX.Element
}

export function PrivateRoute ( { auth, children }: PrivateRouteProps ) {
    // 以登录状态（userData）为准，而非 cookie——session 过期但 cookie 残留时也要拦截
    const ctx = useContext( UserDataContext )
    if ( !ctx ) {
        throw new Error( 'useUserData must be used within a UserDataProvider' )
    }
    return ctx.userData ? children : <Navigate to="/关于网站/介绍" />
}


interface UserDataContextProps {
    userData: UserData | null
    heroData: HeroData | null
    teamData: TeamData | null
    setUserName: ( newUserName: string | null ) => void
    setHeroId: ( newHeroId: string | null ) => void
    setUserData: ( userData: UserData ) => void
    // 直接更新英雄数据（装备保存等场景：setHeroId 相同值不触发刷新，需显式重拉）
    setHeroData: ( heroData: HeroData | null ) => void
}

export const UserDataContext = createContext<UserDataContextProps | undefined>( undefined )

export const UserDataProvider: FC<{ children: ReactNode }> = ( { children } ) => {
    const [ userData, setUserData ] = useState<UserData | null>( () => {
        const storedUserData = localStorage.getItem( "userData" )
        return storedUserData ? JSON.parse( storedUserData ) : null
    } )

    const [ heroData, setHeroData ] = useState<HeroData | null>( () => {
        const storedHeroData = localStorage.getItem( "heroData" )
        return storedHeroData ? JSON.parse( storedHeroData ) : null
    } )

    const [ teamData, setTeamData ] = useState<TeamData | null>( () => {
        const storedTeamData = localStorage.getItem( "teamData" )
        return storedTeamData ? JSON.parse( storedTeamData ) : null
    } )

    const [ userName, setUserNameInternal ] = useState<string | null>( () => Cookies.get( "user" ) || null )
    const [ heroID, setHeroIDInternal ] = useState<string | null>( () => Cookies.get( "hero" ) || null )


    const setUserName = ( newUserName: string | null ) => {
        setUserNameInternal( newUserName )
    }
    const setHeroId = ( newHeroId: string | null ) => {
        setHeroIDInternal( newHeroId )
    }

    useEffect( () => {
        if ( userName !== null ) {
            Cookies.set( "user", userName, { sameSite: "Strict" } )
        } else {
            Cookies.remove( "user" )
            setUserData( null )
        }
        if ( heroID !== null && heroID !== '0' ) {
            Cookies.set( "hero", heroID, { sameSite: "Strict" } )
            api.get( `getHero` )
                .then( response => {
                    const income = response.data
                    console.log( income )
                    setHeroData( income )
                } )
                .catch( error => {
                    console.error( `Error getting Hero ${ heroID } data:`, error )
                } )
        } else {
            Cookies.remove( "hero" )
            setHeroData( null )
        }
        if ( heroData?.team ) {
            api.get( `team` )
                .then( response => {
                    const income = response.data
                    setTeamData( income )
                } )
                .catch( error => {
                    console.error( 'Error getting team:', error )
                } )
        } else {
            setTeamData( null )
        }
    }, [ userName, heroID, heroData?.team ] )


    // 将 UserData 存储到 localStorage
    useEffect( () => {
        if ( userData !== null ) {
            localStorage.setItem( "userData", JSON.stringify( userData ) )
        } else {
            localStorage.removeItem( 'userData' )
            // 统一登录状态：掉登录时清空英雄/团队缓存，避免任何页面显示缓存内容
            setHeroData( null )
            setTeamData( null )
        }
    }, [ userData ] )

    useEffect( () => {
        if ( heroData !== null ) {
            localStorage.setItem( "heroData", JSON.stringify( heroData ) )
        } else {
            localStorage.removeItem( 'heroData' )
        }
    }, [ heroData ] )

    useEffect( () => {
        if ( teamData !== null ) {
            localStorage.setItem( "teamData", JSON.stringify( teamData ) )
        } else {
            localStorage.removeItem( 'teamData' )
        }
    }, [ teamData ] )

    return (
        <UserDataContext.Provider value={ { userData, heroData, teamData, setUserName, setHeroId, setUserData, setHeroData } }>
            { children }
        </UserDataContext.Provider>
    )
}

export function Logout () {
    const ctx = useContext( UserDataContext )
    if ( !ctx ) {
        throw new Error( 'useUserData must be used within a UserDataProvider' )
    }
    ctx.setHeroId( null )
    ctx.setUserName( null )
    Cookies.remove( "session" )
    window.location.href = '/'
    return <div>
        <Alert severity="success">退出成功</Alert>
    </div>
}
